import React, { useState, useEffect } from 'react';
import { 
  FileText, Info, Database, Activity, ShieldCheck, Share2, 
  User, Clock, Users, RefreshCw, Mail, Printer, ArrowUp, 
  MessageCircle, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('information-collected');
  const navigate = useNavigate();

  // ScrollSpy logic to highlight active section in sidebar
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentSection = 'information-collected';
      
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 150) {
          currentSection = section.getAttribute('id');
        }
      });
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Google Font Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        .font-serif {
          font-family: 'Playfair Display', serif;
        }
        
        .font-inter {
          font-family: 'Inter', sans-serif;
        }

        /* Custom Scrollbar for the Sidebar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 4px;
        }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background: #cbd5e1;
        }

        /* Hide elements during print */
        @media print {
          .no-print { display: none !important; }
          .print-full-width { width: 100% !important; max-width: 100% !important; }
          body { background-color: white !important; }
          .shadow-sm, .shadow-md, .shadow-xl { box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
        }
      `}</style>

      <main className="pt-[120px] pb-20 bg-[#f8fafc] min-h-screen font-inter">
        <div className="w-full px-4 lg:px-8 flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          
          {/* Left Sidebar (Sticky & Scrollable) */}
          <aside className="w-[260px] shrink-0 hidden lg:flex flex-col sticky top-[120px] left-0 max-h-[calc(100vh-140px)] overflow-y-auto pb-8 no-print custom-scrollbar">
            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.15em] mb-5 pl-4">
              Contents
            </span>
            <nav className="flex flex-col space-y-1">
              {[
                { id: 'information-collected', label: 'Information Collected' },
                { id: 'how-data-is-used', label: 'How Data Is Used' },
                { id: 'data-protection', label: 'Data Protection' },
                { id: 'data-sharing', label: 'Data Sharing' },
                { id: 'user-rights', label: 'User Rights' },
                { id: 'data-retention', label: 'Data Retention' },
                { id: 'childrens-privacy', label: "Children's Privacy" },
                { id: 'updates', label: 'Updates' },
                { id: 'contact', label: 'Contact' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-left px-4 py-2.5 text-[14px] rounded-xl transition-all ${
                    activeSection === item.id 
                      ? 'text-[#0284c7] font-bold bg-[#f0f9ff]' 
                      : 'text-slate-500 font-medium hover:text-[#0f172a] hover:bg-slate-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <button 
              onClick={handlePrint}
              className="mt-8 w-[calc(100%-16px)] ml-2 py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-[13px] rounded-xl flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              <Printer size={16} /> Print Document
            </button>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 max-w-[1000px] mx-auto bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden print-full-width">
            
            {/* Header / Hero Area inside the card */}
            <div className="p-8 md:p-12 border-b border-slate-100 relative">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-8">
                <div className="w-16 h-16 bg-[#0284c7] rounded-[18px] flex items-center justify-center shadow-lg shadow-sky-500/20 shrink-0">
                  <FileText size={32} className="text-white" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-[36px] md:text-[42px] font-serif text-[#0f172a] font-normal leading-tight mb-3 tracking-tight">
                    Privacy Policy
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-[13px]">
                    <span className="bg-[#e0f2fe] text-[#0284c7] px-3 py-1 rounded-md font-bold uppercase tracking-wider text-[10px]">
                      Official Policy
                    </span>
                    <span className="text-slate-400 font-medium">
                      Updated: February 27, 2026
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Info Box */}
              <div className="bg-[#f0f9ff] border border-[#e0f2fe] rounded-2xl p-5 flex items-start gap-4">
                <Info className="text-[#0284c7] shrink-0 mt-0.5" size={20} />
                <p className="text-[#334155] text-[15px] font-medium leading-relaxed">
                  This page explains your rights, payments, and responsibilities clearly to ensure transparency and trust. We believe in transparent policies and fair usage.
                </p>
              </div>
            </div>

            {/* Content Sections */}
            <div className="p-8 md:p-12 space-y-12 md:space-y-16">
              
              {/* Information Collected */}
              <section id="information-collected" className="scroll-mt-32">
                <SectionHeader icon={<Database size={18} />} title="Information Collected" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-4">
                  We collect the following information to provide and improve our services:
                </p>
                <div className="space-y-2 text-slate-600 text-[15px] leading-relaxed ml-2">
                  <p><strong className="text-slate-800">Name, email, and phone number</strong> for account creation and communication</p>
                  <p><strong className="text-slate-800">Payment details</strong> processed securely through our payment partners</p>
                  <p><strong className="text-slate-800">Internship activity</strong> including task submissions and performance data</p>
                  <p><strong className="text-slate-800">Website usage analytics</strong> to understand how users interact with our platform</p>
                </div>
              </section>

              {/* How Data Is Used */}
              <section id="how-data-is-used" className="scroll-mt-32">
                <SectionHeader icon={<Activity size={18} />} title="How Data Is Used" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-4">
                  Your data is used for the following purposes:
                </p>
                <div className="space-y-2 text-slate-600 text-[15px] leading-relaxed ml-2">
                  <p><strong className="text-slate-800">Service delivery</strong> including internship management and training</p>
                  <p><strong className="text-slate-800">Communication and support</strong> to assist you with any queries</p>
                  <p><strong className="text-slate-800">Certificates and invoices</strong> generation upon completion</p>
                  <p><strong className="text-slate-800">Platform improvement</strong> based on user feedback and usage patterns</p>
                  <p><strong className="text-slate-800">Optional marketing updates</strong> about new opportunities (you can opt-out anytime)</p>
                </div>
              </section>

              {/* Data Protection */}
              <section id="data-protection" className="scroll-mt-32">
                <SectionHeader icon={<ShieldCheck size={18} />} title="Data Protection" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  SENVRA AI uses <strong className="text-slate-800">industry-standard security practices</strong> and restricted access protocols to protect your personal data from unauthorized access, alteration, or disclosure.
                </p>
              </section>

              {/* Data Sharing */}
              <section id="data-sharing" className="scroll-mt-32">
                <SectionHeader icon={<Share2 size={18} />} title="Data Sharing" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Your data is <strong className="text-slate-800">never sold to third parties</strong>. It may be shared only with trusted service providers like payment gateways or hosting partners strictly for the purpose of service delivery.
                </p>
              </section>

              {/* User Rights */}
              <section id="user-rights" className="scroll-mt-32">
                <SectionHeader icon={<User size={18} />} title="User Rights" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  You have the right to request <strong className="text-slate-800">correction, deletion, or access</strong> to your personal data. You may also opt-out of any marketing communications at any time.
                </p>
              </section>

              {/* Data Retention */}
              <section id="data-retention" className="scroll-mt-32">
                <SectionHeader icon={<Clock size={18} />} title="Data Retention" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Data is stored only as long as needed for the fulfillment of our services and to comply with legal and regulatory requirements.
                </p>
              </section>

              {/* Children's Privacy */}
              <section id="childrens-privacy" className="scroll-mt-32">
                <SectionHeader icon={<Users size={18} />} title="Children's Privacy" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Our services are intended for users <strong className="text-slate-800">above 13 years of age</strong>. We do not knowingly collect data from children under this age.
                </p>
              </section>

              {/* Updates */}
              <section id="updates" className="scroll-mt-32">
                <SectionHeader icon={<RefreshCw size={18} />} title="Updates" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  This policy may change anytime to reflect changes in our data practices. Any updates will be posted on this page with a revised "Last Updated" date.
                </p>
              </section>

              {/* Contact */}
              <section id="contact" className="scroll-mt-32">
                <SectionHeader icon={<Mail size={18} />} title="Contact" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-5">
                  If you have any questions regarding your privacy, please contact us at:
                </p>
                <div className="space-y-3">
                  <a href="mailto:contact@senvra.ai" className="flex items-center gap-3 text-[#0284c7] font-medium text-[15px] hover:underline">
                    <Mail size={18} /> contact@senvra.ai
                  </a>
                </div>
              </section>

              {/* Bottom CTA Card */}
              <div className="mt-16 bg-[#f8fafc] border border-slate-200 rounded-[24px] p-10 text-center relative overflow-hidden">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-slate-200 shadow-sm mx-auto mb-5 text-[#0284c7]">
                  <MessageCircle size={22} />
                </div>
                <h3 className="text-[26px] md:text-[28px] font-serif text-[#0f172a] font-bold mb-3 tracking-tight">
                  Still have questions?
                </h3>
                <p className="text-slate-500 text-[15px] mb-8 font-medium">
                  Our team is happy to help clarify anything regarding our policies or services.
                </p>
                <button 
                  onClick={() => navigate('/contact')}
                  className="bg-[#0284c7] hover:bg-[#0369a1] text-white px-8 py-3.5 rounded-xl font-bold text-[14.5px] transition-all flex items-center justify-center gap-2 mx-auto shadow-md"
                >
                  Contact Support <ExternalLink size={16} />
                </button>

                {/* Footer of the CTA Card */}
                <div className="mt-12 pt-6 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                  <div className="flex gap-4">
                    <span>Terms</span>
                    <span>•</span>
                    <span>Privacy</span>
                    <span>•</span>
                    <span>Cookies</span>
                  </div>
                  <div className="flex items-center gap-3 normal-case tracking-normal font-medium text-slate-500 text-[13px]">
                    <a href="mailto:contact@senvra.ai" className="flex items-center gap-1.5 hover:text-[#0284c7] transition-colors">
                      <Mail size={14} /> contact@senvra.ai
                    </a>
                  </div>
                </div>
                <div className="mt-6 flex flex-col md:flex-row justify-center md:justify-between items-center gap-4">
                   <div className="flex items-center gap-2 normal-case tracking-normal font-medium text-slate-500 text-[13px] mx-auto md:mx-0">
                    <ShieldCheck size={16} className="text-emerald-500" /> We believe in transparent policies and fair usage.
                  </div>
                  <button 
                    onClick={scrollToTop}
                    className="flex items-center gap-1 text-[#0284c7] hover:text-[#0369a1] transition-colors font-bold text-[12px] uppercase tracking-wider"
                  >
                    Back to Top <ArrowUp size={14} />
                  </button>
                </div>
              </div>

            </div>
          </div>
          
        </div>

        {/* Floating Back to top for mobile */}
        <button 
          onClick={scrollToTop}
          className="lg:hidden fixed bottom-6 right-6 w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#0284c7] hover:border-[#0284c7] transition-colors z-50"
        >
          <ArrowUp size={20} />
        </button>

      </main>
    </>
  );
};

// Reusable Section Header Component
const SectionHeader = ({ icon, title }) => (
  <div className="flex items-center gap-4 mb-5">
    <div className="w-10 h-10 rounded-[10px] bg-[#f0f9ff] flex items-center justify-center text-[#0284c7] shrink-0 border border-[#e0f2fe]">
      {icon}
    </div>
    <h3 className="font-serif text-[22px] md:text-[24px] text-[#0f172a] font-normal tracking-tight">
      {title}
    </h3>
  </div>
);

export default PrivacyPolicy;